import React, { useState, useCallback, useRef } from 'react';
import SaleMobileCatalog from './SaleMobileCatalog';
import SaleMobileBottomBar from './SaleMobileBottomBar';
import SaleMobileCartDrawer from './SaleMobileCartDrawer';
import SaleMobileCheckout from './SaleMobileCheckout';
import UIDialogConfirm from '../../UI/Dialog/Confirm/UIDialogConfirm';
import PrintPreviewModal from '../Print/PrintPreviewModal';
import { emptyObject, parseNumber } from '../../../utils/utils';
import useApi from '../../../utils/useApi';
import './saleMobile.css';

const SaleNewMobile = ({
  listItens = [],
  setListItens,
  cartValues = {},
  setCartValues,
  calculate = {},
  setCalculate,
  selectClient,
  selectPromotion,
  setDiscount,
  selectPlots,
  resetform,
  searchBarCode,
  credencials,
}) => {
  const [openCartDrawer, setOpenCartDrawer] = useState(false);
  const [openCheckoutDrawer, setOpenCheckoutDrawer] = useState(false);
  const [modalPrint, setModalPrint] = useState(false);
  const [lastFinishedSale, setLastFinishedSale] = useState(null);
  const [alertSuccess, setAlertSuccess] = useState(false);
  const [alertCalculate, setAlertCalculate] = useState(false);
  const [alertCreateSale, setAlertCreateSale] = useState(false);
  const [manualDiscount, setManualDiscount] = useState(0);
  const isCreatingSaleRef = useRef(false);
  const lastItemActionRef = useRef({ time: 0, key: '' });

  // API para cálculo oficial estratégico no servidor
  const [calculateLoad, calculateInfo, setCalculateInfo] = useApi({
    method: 'post',
    url: '/sales',
  });

  // API para finalização da venda
  const [createSaleLoad, createSaleInfo, setCreateSaleInfo] = useApi({
    method: 'post',
    url: '/sales',
  });

  // Cálculo de totais local e instantâneo (estratégia PDV: zero latência de rede)
  const calculateTotalsLocal = useCallback((items, currentDiscount = null) => {
    const subtotal = items.reduce(
      (sum, it) => sum + Number(it.amount || 0) * Number(it.valueUn || 0),
      0
    );
    const roundedSubtotal = Math.round(subtotal * 100) / 100;
    const disc = currentDiscount !== null ? Number(currentDiscount) : Number(cartValues.discount || 0);
    const total = Math.max(0, Math.round((roundedSubtotal - disc) * 100) / 100);

    const productsObj = {};
    const servicesObj = {};
    items.forEach((it) => {
      const safeAmt = Math.round(Number(it.amount || 0) * 10000) / 10000;
      if (it.typeItem === 'product') {
        productsObj[it.id] = (productsObj[it.id] || 0) + safeAmt;
      } else {
        servicesObj[it.id] = (servicesObj[it.id] || 0) + safeAmt;
      }
    });

    if (setCartValues) {
      setCartValues((prev) => ({
        ...prev,
        total,
        totalSum: roundedSubtotal,
        discount: disc,
      }));
    }

    if (setCalculate) {
      setCalculate((prev) => ({
        ...prev,
        products: productsObj,
        services: servicesObj,
      }));
    }
  }, [cartValues.discount, setCartValues, setCalculate]);

  // 1. Adicionar item (Instantâneo / Local com suporte a fracionados)
  const handleSelectItem = (itemData) => {
    const now = Date.now();
    const itemKey = `select-${itemData.id || itemData.barCode || itemData.name}`;
    if (now - lastItemActionRef.current.time < 250 && lastItemActionRef.current.key === itemKey) {
      return;
    }
    lastItemActionRef.current = { time: now, key: itemKey };

    const existingIndex = listItens.findIndex((it) => {
      const sameIdAndType = String(it.id) === String(itemData.id) && it.typeItem === itemData.typeItem;
      const sameBarcode = it.barCode && itemData.barCode && it.barCode === itemData.barCode;
      return sameIdAndType || sameBarcode;
    });

    const parsedQty = Math.max(0.001, Number(itemData.amountSelected || 1));
    const unitPrice = Number(itemData.value || itemData.valueUn || 0);

    let newItems;
    if (existingIndex !== -1) {
      newItems = listItens.map((it, idx) => {
        if (idx === existingIndex) {
          const newAmount = Math.round((Number(it.amount || 0) + parsedQty) * 10000) / 10000;
          return {
            ...it,
            amount: newAmount,
            valueTotal: Math.round((newAmount * Number(it.valueUn || 0)) * 100) / 100,
          };
        }
        return it;
      });
    } else {
      const newItem = {
        id: itemData.id,
        amount: parsedQty,
        name: itemData.name,
        valueUn: unitPrice,
        valueTotal: Math.round((unitPrice * parsedQty) * 100) / 100,
        typeItem: itemData.typeItem,
        barCode: itemData.barCode,
        category: itemData.category ? (typeof itemData.category === 'object' ? itemData.category.id : itemData.category) : null,
      };
      newItems = [...listItens, newItem];
    }

    if (setListItens) {
      setListItens(newItems);
    }
    calculateTotalsLocal(newItems);
  };

  // 2. Editar quantidade (Instantâneo / Local)
  const handleEditItem = (index, newAmount) => {
    const now = Date.now();
    const itemKey = `edit-${index}`;
    if (now - lastItemActionRef.current.time < 250 && lastItemActionRef.current.key === itemKey) {
      return;
    }
    lastItemActionRef.current = { time: now, key: itemKey };

    const parsedAmt = Number(newAmount || 0);
    let newItems;
    if (parsedAmt <= 0) {
      newItems = listItens.filter((_, idx) => idx !== index);
    } else {
      const safeAmt = Math.round(parsedAmt * 10000) / 10000;
      newItems = listItens.map((it, idx) => {
        if (idx === index) {
          return {
            ...it,
            amount: safeAmt,
            valueTotal: Math.round((safeAmt * Number(it.valueUn || 0)) * 100) / 100,
          };
        }
        return it;
      });
    }

    if (setListItens) {
      setListItens(newItems);
    }
    calculateTotalsLocal(newItems);
  };

  // 3. Remover item (Instantâneo / Local)
  const handleRemoveItem = (index) => {
    const now = Date.now();
    const itemKey = `remove-${index}`;
    if (now - lastItemActionRef.current.time < 250 && lastItemActionRef.current.key === itemKey) {
      return;
    }
    lastItemActionRef.current = { time: now, key: itemKey };

    const newItems = listItens.filter((_, idx) => idx !== index);
    if (setListItens) {
      setListItens(newItems);
    }
    calculateTotalsLocal(newItems);
  };

  // 4. Seleção de Cliente (Estratégico: recalcula se houver perfil especial)
  const handleSelectCustomer = (customer) => {
    if (selectClient) {
      selectClient(customer);
    }
    if (setCartValues) {
      setCartValues((prev) => ({ ...prev, customer: customer || null }));
    }
  };

  // 5. Seleção Estratégica de Promoção / Cupom com onlyCalculate
  const handleSelectPromotion = (promo, onErrorCb) => {
    const promoId = promo?.id || 1;
    const productsObj = {};
    const servicesObj = {};
    listItens.forEach((item) => {
      if (item.typeItem === 'product') {
        productsObj[item.id] = (productsObj[item.id] || 0) + Number(item.amount);
      } else {
        servicesObj[item.id] = (servicesObj[item.id] || 0) + Number(item.amount);
      }
    });

    const plotsInt = typeof cartValues.plotsCount === 'number'
      ? cartValues.plotsCount
      : (typeof cartValues.plots === 'number' ? cartValues.plots : 1);

    const payload = {
      onlyCalculate: true,
      cashRegister: credencials?.cashSession || 1,
      customer: cartValues.customer?.id || 1,
      products: productsObj,
      services: servicesObj,
      plots: plotsInt,
      interest: calculate.interest || 0,
      promotion: promoId,
      setDiscount: Number(manualDiscount || 0),
      splitRemaining: 0,
    };

    calculateLoad({
      data: emptyObject(payload),
      onCompleted: (res) => {
        if (res && res.data) {
          setAlertCalculate(false);
          if (setCartValues) {
            setCartValues((prev) => ({
              ...prev,
              ...res.data,
              manualDiscount: manualDiscount,
              plotsTable: typeof res.data.plots === 'object' ? res.data.plots : null,
              plotsCount: plotsInt,
              promotion: promo,
            }));
          }
          if (setCalculate) {
            setCalculate((prev) => ({
              ...prev,
              products: productsObj,
              services: servicesObj,
              plots: plotsInt,
              promotion: promoId,
              setDiscount: Number(manualDiscount || 0),
            }));
          }
        }
      },
      onError: (err) => {
        setAlertCalculate(true);
        if (onErrorCb) {
          onErrorCb(err);
        }
      },
    });
  };

  // 6. Aplicação de Desconto Manual
  const handleSetDiscount = (discStr) => {
    const numDisc = Number(String(discStr || 0).replace(/\./g, '').replace(',', '.'));
    setManualDiscount(numDisc);
    if (setCartValues) {
      setCartValues((prev) => ({ ...prev, manualDiscount: numDisc }));
    }
    if (setCalculate) {
      setCalculate((prev) => ({ ...prev, setDiscount: numDisc }));
    }
    if (setDiscount) {
      setDiscount(discStr);
    }
    calculateTotalsLocal(listItens, numDisc);
  };

  const handleSetObservation = (obs) => {
    if (setCartValues) {
      setCartValues((prev) => ({ ...prev, observation: obs }));
    }
  };

  // 6. Avanço Estratégico para Checkout (Sincronização com Backend / onlyCalculate)
  const handleProceedCheckout = () => {
    const productsObj = {};
    const servicesObj = {};
    listItens.forEach((item) => {
      if (item.typeItem === 'product') {
        productsObj[item.id] = (productsObj[item.id] || 0) + Number(item.amount);
      } else {
        servicesObj[item.id] = (servicesObj[item.id] || 0) + Number(item.amount);
      }
    });

    const plotsInt = typeof cartValues.plotsCount === 'number'
      ? cartValues.plotsCount
      : (typeof cartValues.plots === 'number' ? cartValues.plots : 1);

    const payload = {
      onlyCalculate: true,
      cashRegister: credencials?.cashSession || 1,
      customer: cartValues.customer?.id || 1,
      products: productsObj,
      services: servicesObj,
      plots: plotsInt,
      interest: calculate.interest || 0,
      promotion: cartValues.promotion?.id || 1,
      setDiscount: Number(manualDiscount || 0),
      splitRemaining: 0,
    };

    // Faz a chamada estratégica única para validar totais e promoções no backend
    calculateLoad({
      data: emptyObject(payload),
      onCompleted: (res) => {
        if (res && res.data) {
          setAlertCalculate(false);
          if (setCartValues) {
            setCartValues((prev) => ({
              ...prev,
              ...res.data,
              plotsTable: typeof res.data.plots === 'object' ? res.data.plots : null,
              plotsCount: plotsInt,
            }));
          }
          setOpenCartDrawer(false);
          setOpenCheckoutDrawer(true);
        }
      },
      onError: () => {
        setAlertCalculate(true);
      },
    });
  };

  // 7. Finalização da Venda
  const handleFinishSale = (paymentData, plots, interestParam) => {
    if (isCreatingSaleRef.current || createSaleInfo.loading) return;
    isCreatingSaleRef.current = true;

    const productsObj = {};
    const servicesObj = {};
    listItens.forEach((item) => {
      if (item.typeItem === 'product') {
        productsObj[item.id] = (productsObj[item.id] || 0) + Number(item.amount);
      } else {
        servicesObj[item.id] = (servicesObj[item.id] || 0) + Number(item.amount);
      }
    });

    const plotsInt = typeof plots === 'number'
      ? plots
      : (typeof cartValues.plotsCount === 'number' ? cartValues.plotsCount : (typeof cartValues.plots === 'number' ? cartValues.plots : 1));

    const interestObj = typeof interestParam === 'object'
      ? interestParam
      : (Number(interestParam || 0) > 0 ? { percentage: Number(interestParam), type: 'simples' } : (calculate.interest || {}));

    const formattedPaymentData = (paymentData || []).map((p) => ({
      paymentMethod: String(p.paymentMethod),
      amountPaid: typeof p.amountPaid === 'string'
        ? p.amountPaid.replace(/\./g, '').replace(',', '.')
        : String(p.amountPaid || 0),
    }));

    const customerId = Number(cartValues.customer?.id || (typeof cartValues.customer === 'number' ? cartValues.customer : 1)) || 1;
    const promoId = Number(cartValues.promotion?.id || (typeof cartValues.promotion === 'number' ? cartValues.promotion : 1)) || 1;

    const payload = {
      onlyCalculate: false,
      cashRegister: Number(credencials?.cashSession || 1),
      customer: customerId,
      products: productsObj,
      services: servicesObj,
      plots: plotsInt,
      interest: interestObj,
      promotion: promoId,
      setDiscount: Number(manualDiscount || 0) > 0 ? Number(manualDiscount) : undefined,
      observation: cartValues.observation || '',
      paymentData: formattedPaymentData,
      splitRemaining: false,
    };

    createSaleLoad({
      data: {
        ...emptyObject(payload),
        onlyCalculate: false,
        plots: plotsInt,
        customer: customerId,
        promotion: promoId,
        paymentData: formattedPaymentData,
      },
      onCompleted: (res) => {
        if (res && res.data) {
          setLastFinishedSale(res.data);
          setAlertSuccess(true);
          setAlertCreateSale(false);
          setOpenCheckoutDrawer(false);
          setModalPrint(true);
          if (resetform) {
            resetform();
          }
        } else {
          isCreatingSaleRef.current = false;
        }
      },
      onError: () => {
        isCreatingSaleRef.current = false;
        setAlertCreateSale(true);
      },
    });
  };

  return (
    <div className="sale-mobile-wrapper">
      {/* 1. Catálogo Rápido com Busca e Infinite Scroll */}
      <SaleMobileCatalog
        listItens={listItens}
        onSelectItem={handleSelectItem}
        onEditItem={handleEditItem}
        onRemoveItem={handleRemoveItem}
        onBarCodeSubmit={searchBarCode}
      />

      {/* 2. Barra Fixa Inferior com Totalizador */}
      <SaleMobileBottomBar
        listItens={listItens}
        total={cartValues.total}
        onOpenCart={() => setOpenCartDrawer(true)}
      />

      {/* 3. Gaveta do Carrinho (Bottom Sheet) */}
      <SaleMobileCartDrawer
        open={openCartDrawer}
        onClose={() => setOpenCartDrawer(false)}
        listItens={listItens}
        cartValues={cartValues}
        manualDiscount={manualDiscount}
        onEditItem={handleEditItem}
        onRemoveItem={handleRemoveItem}
        onSelectCustomer={handleSelectCustomer}
        onSelectPromotion={handleSelectPromotion}
        onSetDiscount={handleSetDiscount}
        onSetObservation={handleSetObservation}
        onProceedCheckout={handleProceedCheckout}
        alertCalculate={alertCalculate}
        setAlertCalculate={setAlertCalculate}
        calculateInfo={calculateInfo}
        setCalculateInfo={setCalculateInfo}
      />

      {/* 4. Checkout e Pagamento */}
      <SaleMobileCheckout
        open={openCheckoutDrawer}
        onClose={() => setOpenCheckoutDrawer(false)}
        cartValues={cartValues}
        onFinishSale={handleFinishSale}
        loading={createSaleInfo.loading || calculateInfo.loading}
      />

      {/* 5. Alertas de Sucesso e Erro */}
      <UIDialogConfirm
        open={alertSuccess}
        data={createSaleInfo}
        timeSucess={3000}
        messageSucess="Venda finalizada com sucesso!"
        close={() => setAlertSuccess(false)}
        setrequestinfo={setCreateSaleInfo}
      />

      <UIDialogConfirm
        open={alertCalculate}
        data={calculateInfo}
        close={() => setAlertCalculate(false)}
        setrequestinfo={setCalculateInfo}
      />

      <UIDialogConfirm
        open={alertCreateSale}
        data={createSaleInfo}
        close={() => setAlertCreateSale(false)}
        setrequestinfo={setCreateSaleInfo}
      />

      {/* 6. Modal de Impressão */}
      {lastFinishedSale && (
        <PrintPreviewModal
          open={modalPrint}
          handleClose={() => setModalPrint(false)}
          saleData={lastFinishedSale}
          credencials={credencials}
          options={{ customer: cartValues.customer }}
        />
      )}
    </div>
  );
};

export default SaleNewMobile;
